import { ENDPOINTS, getFullUrl } from './apiConfig';

/**
 * Fetches all users for a given company from the API.
 * @param {string|number} companyId - The company ID.
 * @returns {Promise<Array<object>>} - A promise that resolves with an array of user objects.
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const getAllUsers = async (companyId) => {
  if (!companyId) throw new Error('Company ID is required to fetch users.');
  const endpoint = ENDPOINTS.GET_ALL_USERS_BY_COMPANY.replace(':company_id', companyId);
  const url = getFullUrl(endpoint);
  console.log('Sending GET request to fetch users by company:', url);

  const token = localStorage.getItem('accessToken');
  
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('No access token found for fetching users.');
    // Depending on your API, you might need to throw an error if auth is strictly required
    throw new Error('Authentication required.'); 
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
         console.error('API Error: Unauthorized fetching users.');
         throw new Error(`API Error: ${response.status} Unauthorized - Please log in again.`);
      }
      // Try to parse error details, default to empty object if parsing fails
      const errorDetails = await response.json().catch(() => ({})); 
      console.error('API Error Response (Users):', response.status, response.statusText, errorDetails);
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorDetails.detail || 'Failed to fetch users.'}`);
    }

    const result = await response.json();
    console.log('API Success Response (Users):', result);
    return result; 

  } catch (error) {
    console.error('Network or Fetch Error fetching users:', error);
    // Re-throw API errors directly, wrap others
    if (error.message.startsWith('API Error:')) {
      throw error; 
    } else {
      throw new Error(`Network error: ${error.message}`);
    }
  }
};

/**
 * Registers a new user via the API.
 * @param {object} userData - The user data { email, first_name, last_name, password }.
 * @returns {Promise<object>} - A promise that resolves with the API response data.
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const registerUser = async (userData) => {
  const url = getFullUrl(ENDPOINTS.REGISTER_USER);
  console.log('Sending POST request to register user:', url);
  console.log('Payload:', JSON.stringify(userData, null, 2));

  const headers = {
    'Content-Type': 'application/json',
  };

  // Ensure company_id is included in the payload
  if (!userData.company_id) {
    // Try to get company_id from localStorage or throw an error
    const userStr = localStorage.getItem('userData');
    let company_id = '';
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        company_id = userObj?.company?.id || userObj?.company_id || '';
      } catch {}
    }
    if (!company_id) {
      throw new Error('company_id is required to register a user.');
    }
    userData.company_id = company_id;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorDetails = await response.json().catch(() => ({})); 
      console.error('API Error Response (Register User):', response.status, response.statusText, errorDetails);
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      if (errorDetails.detail) {
        errorMessage += ` - ${errorDetails.detail}`;
      } else if (typeof errorDetails === 'object' && Object.keys(errorDetails).length > 0) {
        errorMessage += ` - ${JSON.stringify(errorDetails)}`;
      } else {
        errorMessage += ' - Failed to register user.';
      }
      throw new Error(errorMessage);
    }

    const result = await response.json().catch(() => ({}));
    console.log('API Success Response (Register User):', result);
    return result; 

  } catch (error) {
    console.error('Network or Fetch Error registering user:', error);
    if (error.message.startsWith('API Error:')) {
      throw error; 
    } else {
      throw new Error(`Network error: ${error.message}`);
    }
  }
};

/**
 * Deletes a user via the API.
 * @param {number|string} userId - The ID of the user to delete.
 * @returns {Promise<object>} - A promise that resolves with the API response (often empty on success).
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const deleteUser = async (userId) => {
  // Replace the placeholder in the endpoint with the actual user ID
  const endpoint = ENDPOINTS.DELETE_USER.replace(':userId', userId);
  const url = getFullUrl(endpoint);
  console.log(`Sending DELETE request to delete user ${userId}:`, url);

  const token = localStorage.getItem('accessToken');
  
  const headers = {
    'Content-Type': 'application/json', // Although no body, content-type might still be expected
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn(`No access token found for deleting user ${userId}.`);
    throw new Error('Authentication required.'); 
  }

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: headers,
    });

    // Check if the response status indicates success (e.g., 204 No Content)
    if (!response.ok) {
       if (response.status === 401) {
         console.error(`API Error: Unauthorized deleting user ${userId}.`);
         throw new Error(`API Error: ${response.status} Unauthorized - Please log in again.`);
      }
       if (response.status === 404) {
         console.error(`API Error: User ${userId} not found.`);
         throw new Error(`API Error: ${response.status} User not found.`);
      }
      // Attempt to parse other errors
      const errorDetails = await response.json().catch(() => ({})); 
      console.error(`API Error Response (Delete User ${userId}):`, response.status, response.statusText, errorDetails);
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorDetails.detail || 'Failed to delete user.'}`);
    }

    // DELETE requests often return 204 No Content, so no JSON body is expected.
    console.log(`API Success: User ${userId} deleted.`);
    // Return an empty object or status for consistency, as response.json() would fail on 204
    return { status: response.status, statusText: response.statusText }; 

  } catch (error) {
    console.error(`Network or Fetch Error deleting user ${userId}:`, error);
    if (error.message.startsWith('API Error:')) {
      throw error; 
    } else {
      throw new Error(`Network error: ${error.message}`);
    }
  }
};

/**
 * Updates a user's details via the API using PATCH.
 * @param {number|string} userId - The ID of the user to update.
 * @param {object} userData - The user data to update { email, first_name, last_name }. Password update might need a separate mechanism.
 * @returns {Promise<object>} - A promise that resolves with the updated user data.
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const updateUser = async (userId, userData) => {
  const endpoint = ENDPOINTS.UPDATE_USER.replace(':userId', userId);
  const url = getFullUrl(endpoint);
  console.log(`Sending PUT request to update user ${userId}:`, url);
  console.log('Payload:', JSON.stringify(userData, null, 2));

  const token = localStorage.getItem('accessToken');
  
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn(`No access token found for updating user ${userId}.`);
    throw new Error('Authentication required.'); 
  }

  try {
    const response = await fetch(url, {
      method: 'PUT', // Use PUT to match backend route
      headers: headers,
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error(`API Error: Unauthorized updating user ${userId}.`);
        throw new Error(`API Error: ${response.status} Unauthorized - Please log in again.`);
      }
      if (response.status === 404) {
        console.error(`API Error: User ${userId} not found for update.`);
        throw new Error(`API Error: ${response.status} User not found.`);
      }
      const errorDetails = await response.json().catch(() => ({})); 
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      if (errorDetails.detail) {
        errorMessage += ` - ${errorDetails.detail}`;
      } else if (typeof errorDetails === 'object' && Object.keys(errorDetails).length > 0) {
        errorMessage += ` - ${JSON.stringify(errorDetails)}`;
      } else {
        errorMessage += ' - Failed to update user.';
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(`API Success Response (Update User ${userId}):`, result);
    return result; 

  } catch (error) {
    console.error(`Network or Fetch Error updating user ${userId}:`, error);
    if (error.message.startsWith('API Error:')) {
      throw error; 
    } else {
      throw new Error(`Network error: ${error.message}`);
    }
  }
};

// Add other user-related API functions here later (e.g., updateUser)
